/* eslint-disable react/no-array-index-key */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Stack,
  Drawer,
  Collapse,
  Divider,
  Button,
} from '@mui/material';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { ref, update } from 'firebase/database';
import { toast } from 'react-toastify';
import axios from 'axios';
import { baseUrl } from 'Api';
import { AppContext, AuthContext } from 'Context';
import { Pathname } from 'Routes';
import {
  removeDummy,
  clearAllDummys,
  saveDummy,
  saveUploadInfo,
  clearUploadInfo,
} from 'store/store';
import { ReactComponent as DashboardIcon } from 'Assets/home.svg';
import { ReactComponent as StorageIcon } from 'Assets/Uploader.svg';
import { ReactComponent as VideosIcon } from 'Assets/videos.svg';
import { ReactComponent as ContentIcon } from 'Assets/content.svg';
import { ReactComponent as SettingsIcon } from 'Assets/settings.svg';
import { ReactComponent as Viewers } from 'Assets/viewer.svg';
import { Logo } from 'Components/logo';
import { ConfirmDialog } from 'Components/ConfirmDialog';
import uploadManager from 'Utils/uploadManagerSecure';
import {
  LayoutDashboard, Film, LayoutGrid, CloudUpload, Users, Settings as SettingsIco, Projector, UserRoundPlus,
} from 'Utils/lucideIcons';
import { V2ThemeProvider } from './V2Layout';
import Dock from './Dock';
import AccessActionsFab from './AccessActionsFab';

// Lucide icon for each nav destination (keyed by the item's link prefix).
const wheelIconFor = (link) => {
  if (link === Pathname.access) return Projector;
  if (link === Pathname.dashboard) return LayoutDashboard;
  if (link === Pathname.yourVideos) return Film;
  if (link === Pathname.contentLayout) return LayoutGrid;
  if (link?.startsWith('/storage')) return CloudUpload;
  if (link?.startsWith('/viewers')) return Users;
  if (link?.startsWith('/settings')) return SettingsIco;
  return LayoutDashboard;
};

// Slim peek strip — just wide enough for the current section's icon. The full
// option-wheel slides in over the content on hover, so this is all the
// permanent horizontal space the sidebar takes.
// Navigation now lives in a bottom dock, so pages no longer reserve a left rail.
const SIDEBAR_WIDTH_PX = {
  xs: 0,
  md: 0,
  lg: 0,
  xl: 0,
};

const linkRowSx = (active) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  px: 1.25,
  py: 1.05,
  borderRadius: 2,
  cursor: 'pointer',
  color: active ? 'primary.main' : 'text.primary',
  bgcolor: active ? 'action.hover' : 'transparent',
  fontWeight: active ? 600 : 500,
  fontSize: 14,
  textDecoration: 'none',
  transition: 'background-color 120ms ease, color 120ms ease',
  '&:hover': { bgcolor: 'action.hover' },
});

const ExpandableGroup = ({ icon: Icon, label, link, childLinks, currentPath, defaultOpen }) => {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const isParentActive = currentPath === link
    || childLinks?.some(({ link: cl }) => currentPath === cl);

  return (
    <Box>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{
          ...linkRowSx(isParentActive),
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
          <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { width: 18, height: 18 } }}>
            <Icon />
          </Box>
          <Box component="span">{label}</Box>
        </Stack>
        {open
          ? <ExpandLessRoundedIcon sx={{ fontSize: 18 }} />
          : <ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />}
      </Box>
      <Collapse in={open} timeout={180} unmountOnExit>
        <Stack spacing={0.25} sx={{ mt: 0.5, pl: 4 }}>
          {childLinks.map(({ name, link: cl }, idx) => {
            const active = currentPath === cl;
            return (
              <NavLink
                key={idx}
                to={cl}
                style={{ textDecoration: 'none' }}
              >
                <Box
                  sx={{
                    ...linkRowSx(active),
                    fontSize: 13,
                    py: 0.85,
                  }}
                >
                  {name}
                </Box>
              </NavLink>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
};

const buildLinks = (sideBarText, plan, isInActiveUser) => {
  const access = {
    name: 'Projectors',
    sidebarName: 'Projectors',
    icon: VideosIcon,
    link: Pathname.access,
  };
  const dashboard = {
    name: sideBarText?.dashboard || 'Dashboard',
    sidebarName: 'Dashboard',
    icon: DashboardIcon,
    link: Pathname.dashboard,
  };
  const content = {
    name: sideBarText?.content || 'Content',
    sidebarName: 'Video List',
    icon: VideosIcon,
    link: Pathname.yourVideos,
  };
  const layout = {
    name: sideBarText?.layout || 'Layout',
    sidebarName: 'Content Layout',
    icon: ContentIcon,
    link: Pathname.contentLayout,
  };
  const uploader = {
    name: 'Uploader',
    sidebarName: 'Uploader',
    icon: StorageIcon,
    link: Pathname.getStoragePath(Pathname.storage.videos),
    childLinks: [
      { name: 'Cloud', link: Pathname.getViewersPath(Pathname.viewers.cloud) },
    ],
  };
  const viewers = {
    name: sideBarText?.viewers || 'Viewers',
    sidebarName: 'Viewers',
    icon: Viewers,
    link: Pathname.getViewersPath(Pathname.viewers.viewer),
    childLinks: [
      { name: 'Viewers', link: Pathname.getViewersPath(Pathname.viewers.viewer) },
      { name: 'Groups', link: Pathname.getViewersPath(Pathname.viewers.groups) },
      { name: 'Successors', link: Pathname.getViewersPath(Pathname.viewers.successors) },
      { name: 'Connected Accounts', link: Pathname.getViewersPath(Pathname.viewers.connectedAccounts) },
    ],
  };

  const inactiveSettings = {
    name: sideBarText?.settings || 'Settings',
    sidebarName: 'Account Settings',
    icon: SettingsIcon,
    link: Pathname.getSettingsPath(Pathname.settings.billings),
    childLinks: [
      { name: 'Privacy', link: Pathname.getSettingsPath(Pathname.settings.privacy) },
      { name: 'Billing and Payments', link: Pathname.getSettingsPath(Pathname.settings.billings) },
    ],
  };

  const settings = {
    name: sideBarText?.settings || 'Settings',
    sidebarName: 'Account Settings',
    icon: SettingsIcon,
    link: Pathname.settings.basePath,
    childLinks: [
      { name: sideBarText?.accountSideBarTitle || 'Account', link: Pathname.getSettingsPath(Pathname.settings.accounts) },
      { name: sideBarText?.notificationSidebarTitle || 'Notifications', link: Pathname.getSettingsPath(Pathname.settings.notifications) },
      { name: 'Billing and Payments', link: Pathname.getSettingsPath(Pathname.settings.billings) },
      { name: sideBarText?.privacySidebarTitle || 'Privacy', link: Pathname.getSettingsPath(Pathname.settings.privacy) },
      { name: sideBarText?.termsSidebarTitle || 'Terms of Use', link: Pathname.getSettingsPath(Pathname.settings.termsAndConditions) },
      { name: sideBarText?.subscriberSidebarTitle || 'Subscriber Agreement', link: Pathname.getSettingsPath(Pathname.settings.subscriberAgreement) },
    ],
  };

  if (plan === 'Free') {
    return [access, dashboard, content, layout, uploader, viewers, settings];
  }
  if (isInActiveUser) {
    return [access, dashboard, inactiveSettings];
  }
  return [access, dashboard, content, layout, uploader, viewers, settings];
};

const V2SidebarInner = ({ onClose, sidebarIsVisible, noFixedSidebar }) => {
  const {
    iamUploading,
    langText,
    fetchData,
    setEncounterLoading,
    userAccessedData,
    handleManger,
    getSubscription,
    autoSignIn,
    getAccessChecker,
    setGetAccessChecker,
    firebaseDatabase,
    setUploadContainer,
    subscriptionDetails,
    setIamUploading,
    draftOfUpload,
    setActiveUploads,
    setUploadQueue,
    setOpen,
    handleCancelAllDownloads,
    setAppSnackbar,
  } = useContext(AppContext);
  const { isInActiveUser, getToken } = useContext(AuthContext);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const uploadPercentages = useSelector((state) => state.upload);

  const [isOpenInfoModal, setInfoModal] = useState(false);
  const [exitTitle, setExitTitle] = useState('You’ll stop accessing this account and return to your own.');
  const [responseDelay, setResponseDelay] = useState(false);
  // The access pill aligns its left edge with the centered floating dock. The
  // dock width is dynamic (per page + magnify), so measure the real panel and
  // sit the pill just above it, left edges flush.
  const [dockPos, setDockPos] = useState(null);

  const sideBarText = langText?.sidebar;
  const links = useMemo(
    () => buildLinks(sideBarText, subscriptionDetails?.current_plan, isInActiveUser),
    [sideBarText, subscriptionDetails?.current_plan, isInActiveUser],
  );
  // Nav items for the desktop option-wheel — Lucide icon + short label + route.
  const wheelItems = useMemo(
    () => links.map((l) => ({
      icon: wheelIconFor(l.link),
      label: l.name || l.sidebarName,
      link: l.link,
    })),
    [links],
  );
  const activeWheelIndex = Math.max(0, wheelItems.findIndex(
    (it) => location.pathname === it.link
      || (it.link !== '/' && location.pathname.startsWith(`${it.link}/`)),
  ));

  const postData = async () => {
    try {
      const formData = new FormData();
      const myToken = getToken();
      formData.append('token', myToken);
      const response = await fetch(`${baseUrl}/getUserAccess`, { method: 'POST', body: formData });
      if (response.ok) {
        const data = await response.json();
        setGetAccessChecker(data);
        return data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error:', error.message);
    }
    return null;
  };

  useEffect(() => {
    postData();
  }, [userAccessedData]); // eslint-disable-line

  // Track the floating dock's on-screen box so the access pill can align its
  // left edge with the dock and sit just above it. Re-measures on resize and
  // route change (the dock gains/loses items per page).
  useEffect(() => {
    if (getAccessChecker?.success !== true) return undefined;
    const measure = () => {
      const el = document.querySelector('.dock-panel');
      // No dock on this route (e.g. /access uses the floating + button) — drop
      // any position measured on the previous page so the pill falls back to
      // the bottom-left corner instead of floating at a stale offset.
      if (!el) { setDockPos(null); return; }
      const r = el.getBoundingClientRect();
      setDockPos({
        left: Math.round(r.left),
        bottom: Math.round(window.innerHeight - r.top + 12),
      });
    };
    measure();
    const raf = requestAnimationFrame(measure);
    const t = setTimeout(measure, 350);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      window.removeEventListener('resize', measure);
    };
  }, [getAccessChecker?.success, location.pathname]);

  const databaseRef = ref(firebaseDatabase, 'status_from_sns');
  const changeFieldFromFirebase = (draft_id, statusString) => {
    update(databaseRef, { [draft_id]: { status: statusString } });
  };
  const updateUploadPercentages = (draft_id, data) => {
    dispatch(saveUploadInfo({ [draft_id]: data }));
  };
  const updateDummy = (dummy, status) => {
    if (status === 1) dispatch(saveDummy({ [dummy?.draft_id]: dummy }));
    else dispatch(removeDummy(dummy));
  };
  const closeToast = (draft_id, message, type) => {
    toast.update(draft_id, {
      closeButton: false,
      render: message,
      type: type === 'success' ? toast.TYPE.SUCCESS : toast.TYPE.ERROR,
      autoClose: 3000,
    });
    setUploadContainer((prev) => prev.filter((id) => id !== draft_id));
  };

  const deleteAdded = () => {
    setEncounterLoading(true);
    draftOfUpload?.forEach((draftId) => {
      const uploadStatus = uploadPercentages[draftId];
      setActiveUploads([]);
      setUploadQueue([]);
      if (uploadStatus !== 'Please wait') {
        changeFieldFromFirebase(draftId, 'DELETED');
        updateDummy(draftId, 0);
        updateUploadPercentages(draftId, 'Cancelled');
        closeToast(draftId, 'Upload has been canceled by user', 'error');
      }
    });
  };

  const managerReliving = async () => {
    try {
      // getAccessChecker is fetched once per access session, so it can be stale
      // (or shaped without a user_token) by the time Exit is clicked. Refetch
      // before giving up on it.
      let accessData = getAccessChecker;
      if (!accessData?.data?.user_token) {
        accessData = await postData();
      }
      const formData = new FormData();
      formData.append('token', accessData?.data?.user_token || getToken());
      const response = await axios.post(`${baseUrl}/getManagerUserLogs`, formData);
      // Restore the manager FIRST. handleManger returns false when no usable
      // manager token came back (stale access-log row), and in that case we
      // leave the session exactly as it is instead of clearing the identity /
      // impersonation markers — the manager stays inside the account and can
      // retry rather than being logged out with everything wiped.
      if (handleManger(response.data) === false) {
        setResponseDelay(false);
        setInfoModal(false);
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: 'Could not switch back to your account. Please try again.',
        });
        return;
      }
      localStorage.setItem('firstname', '');
      localStorage.setItem('lastname', '');
      localStorage.removeItem('isAccessingUser');
      localStorage.removeItem('accessedUserId');
      dispatch(clearAllDummys());
      setResponseDelay(true);
      await postData();
      await autoSignIn();
      await getSubscription();
      setInfoModal(false);
      setResponseDelay(false);
      dispatch(clearUploadInfo());
      sessionStorage.removeItem('quickUploadVisited');
      sessionStorage.removeItem('quickUserEmail');
      navigate('/settings/manage-users');
    } catch (error) {
      setResponseDelay(false);
      setInfoModal(false);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const handleExit = async () => {
    setOpen(false);
    if (uploadManager.uploads) {
      uploadManager.uploads.forEach((task) => {
        if (task && task.id) {
          // eslint-disable-next-line no-param-reassign
          task.updateUploadPercentages = null;
          // eslint-disable-next-line no-param-reassign
          task.suppressToast = true;
          // eslint-disable-next-line no-param-reassign
          task.onSuccess = null;
          // eslint-disable-next-line no-param-reassign
          task.onError = null;
          uploadManager.cancelUpload(task.id);
        }
      });
    }
    if (handleCancelAllDownloads) handleCancelAllDownloads();
    deleteAdded();
    dispatch(clearUploadInfo());
    setIamUploading(true);
    await managerReliving();
    fetchData();
  };

  const exitFrom = () => {
    setInfoModal(true);
    const uploading = Object.values(uploadPercentages).some(
      (value) => typeof value === 'number' || value === 'Starting Upload',
    );
    setExitTitle(uploading
      ? 'Any in-progress uploads will be cancelled if you exit.'
      : 'You’ll stop accessing this account and return to your own.');
  };

  const currentPath = location.pathname;
  const onAccessPage = currentPath === Pathname.access;

  return (
    <>
      <ConfirmDialog
        open={isOpenInfoModal}
        icon={<LogoutRoundedIcon />}
        tone="danger"
        title="Exit this account?"
        description={exitTitle}
        confirmLabel="Exit"
        cancelLabel="Cancel"
        loading={responseDelay}
        onConfirm={() => handleExit()}
        onCancel={() => setInfoModal(false)}
      />

      {/* Primary navigation: a bottom dock, always visible. Replaces the side
          rail. Suppressed when the host page sets noFixedSidebar, and on
          /access — the client wants that screen free of the tab bar, so its
          contents move into the floating + button below. */}
      {!noFixedSidebar && !onAccessPage && (
        <Dock
          panelHeight={68}
          baseItemSize={50}
          magnification={78}
          distance={200}
          items={wheelItems.map((it, i) => {
            const Ic = it.icon;
            return {
              icon: <Ic size={22} />,
              label: it.label,
              // reactbits Dock highlights via per-item className, not an `active` prop.
              className: i === activeWheelIndex ? 'dock-item--active' : '',
              onClick: () => navigate(it.link),
            };
          })}
        />
      )}

      {/* /access: no bottom tab bar. A single floating + button rolls the two
          projector actions (previously the dock's three-dot menu) out to the
          right, and rolls them back when it turns into an ×. */}
      {!noFixedSidebar && onAccessPage && (
        <AccessActionsFab
          label="Projector actions"
          actions={[
            {
              key: 'request',
              label: 'Request New Access',
              icon: UserRoundPlus,
              onClick: () => navigate(`${Pathname.access}${Pathname.requestAccess}`),
            },
            {
              key: 'manage',
              label: 'Manage Projectors',
              icon: LayoutGrid,
              onClick: () => window.dispatchEvent(new CustomEvent('access:toggle-manage')),
            },
          ]}
        />
      )}

      {/* Manager-accessed indicator. A full-width bottom bar collided with the
          centered floating dock (z-index 1250), so this is a compact glass pill
          a glass pill whose left edge is flush with the centered floating dock,
          sitting just above it. The amber pulse signals you're acting inside
          someone else's account.
          Gated on BOTH the backend's /getUserAccess response AND the frontend
          marker set only when the user actually clicked "Access" in
          /settings/manage-users — matching every other copy of this pattern
          in the codebase (Components/sidebar, header, BasicAccordion,
          CreateUser, ManagerList, UserManger). Without the marker, a stale or
          over-permissive /getUserAccess response could trip the banner on a
          normal login (which is exactly what was happening here). */}
      {getAccessChecker?.success === true
        && localStorage.getItem('isAccessingUser') === 'true' && (
        <Box
          sx={{
            position: 'fixed',
            // Left edge flush with the floating dock; sits just above it. Falls
            // back to the bottom-left corner until the dock is measured.
            left: dockPos ? `${dockPos.left}px` : { xs: 12, md: 16 },
            bottom: dockPos ? `${dockPos.bottom}px` : { xs: 88, md: 16 },
            maxWidth: 'calc(100vw - 32px)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            pl: 1.75,
            pr: 0.75,
            py: 0.75,
            borderRadius: 999,
            bgcolor: 'rgba(15,23,42,0.94)',
            color: '#f5f5f7',
            border: '1px solid rgba(245,158,11,0.45)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxShadow: '0 12px 34px rgba(0,0,0,0.42)',
          }}
        >
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#f59e0b',
              boxShadow: '0 0 0 0 rgba(245,158,11,0.55)',
              animation: 'v2AccessPulse 1.8s ease-out infinite',
              '@keyframes v2AccessPulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(245,158,11,0.55)' },
                '70%': { boxShadow: '0 0 0 7px rgba(245,158,11,0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(245,158,11,0)' },
              },
            }}
          />
          <Box component="span" sx={{ fontSize: 13, fontWeight: 500, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Viewing
            {' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {(userAccessedData?.firstname || localStorage.getItem('firstname') || '')}
              {' '}
              {(userAccessedData?.lastname || localStorage.getItem('lastname') || '')}
            </Box>
            ’s account
          </Box>
          <Button
            size="small"
            variant="contained"
            startIcon={<LogoutRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={exitFrom}
            sx={{
              flexShrink: 0,
              bgcolor: '#fff',
              color: '#0f172a',
              '&:hover': { bgcolor: '#e2e8f0' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12.5,
              borderRadius: 999,
              px: 1.5,
              py: 0.4,
              minWidth: 0,
              boxShadow: 'none',
            }}
          >
            Exit
          </Button>
        </Box>
      )}

      {/* Mobile drawer */}
      <Drawer
        anchor="left"
        open={Boolean(sidebarIsVisible)}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: 'background.paper',
            color: 'text.primary',
            backgroundImage: 'none',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 0.5 }}>
            <Logo />
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          <Stack spacing={0.5} onClick={onClose}>
            {links.map(({ icon: Icon, link, sidebarName, childLinks }, idx) => {
              if (!childLinks) {
                const active = currentPath === link;
                return (
                  <NavLink
                    key={idx}
                    to={link}
                    style={{ textDecoration: 'none' }}
                  >
                    <Box sx={linkRowSx(active)}>
                      <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { width: 18, height: 18 } }}>
                        <Icon />
                      </Box>
                      <Box component="span">{sidebarName}</Box>
                    </Box>
                  </NavLink>
                );
              }
              return (
                <ExpandableGroup
                  key={idx}
                  icon={Icon}
                  label={sidebarName}
                  link={link}
                  childLinks={childLinks}
                  currentPath={currentPath}
                  defaultOpen={
                    currentPath === link
                    || childLinks.some(({ link: cl }) => currentPath === cl)
                  }
                />
              );
            })}
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

// Skip CssBaseline here — the sidebar mounts on every page when new UI
// is enabled (including classic pages like /profile). Letting MUI's
// global reset run there crushes legacy layouts (e.g. Swiper slide gaps
// on the profile category strip). V2 pages still get the baseline via
// their own V2ThemeProvider / V2Page wrappers.
export const V2Sidebar = (props) => (
  <V2ThemeProvider withBaseline={false}>
    <V2SidebarInner {...props} />
  </V2ThemeProvider>
);

export const V2_SIDEBAR_WIDTH_PX = SIDEBAR_WIDTH_PX;
